import { createColumnBuilder } from "../../../components/table/columnBuilder";
import { type TargetQueryV4Row } from "../api/target/v4TargetReportByTarget";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useStandardEntityMetrics } from "../../merchandising/components/tables/metricUtils";
import { useDateFormatter } from "../../../i18n/useDateFormatter";
import { snakeCaseToSentenceCase } from "../../../utils/formatText";
import { getTargetBidStateLabel } from "../../str/utils/reportingConstants";
import { MATCH_TYPE_SELECTORS, TARGET_STATE_SELECTORS } from "../../advertising/filters/targetLifecycleFilters";

const columnHelper = createColumnBuilder<TargetQueryV4Row>();

export const useBaseTargetColumns = (targetType: string) => {
  const standardMetricIds = useStandardEntityMetrics("target");
  const { t: tStr } = useTranslation("str");
  const { t } = useTranslation(["perf_reports", "advertising"]);
  const dateFormatter = useDateFormatter("medium");

  return useMemo(
    () => [
      columnHelper.accessor("match_type", {
        id: "match_type",
        header: t(($) => $.perf_reports["Match Type"]),
        cell: ({ getValue }) => {
          const raw = getValue();
          const selector = MATCH_TYPE_SELECTORS[raw];
          if (selector) return <span>{t(selector)}</span>;
          const fallback = raw
            .replaceAll(/([a-z])([A-Z])/g, "$1 $2")
            .toLowerCase()
            .replace("_", " ");
          return <span>{fallback}</span>;
        },
        size: 100,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
          columnSelectorOptions: {
            grouping: "configuration",
            description: t(($) => $.perf_reports["Target match type"]),
          },
        },
      }),
      columnHelper.accessor("state", {
        id: "state",
        header: t(($) => $.perf_reports["State"]),
        size: 100,
        cell: ({ getValue }) => {
          const raw = getValue();
          const selector = TARGET_STATE_SELECTORS[raw];
          return selector ? t(selector) : snakeCaseToSentenceCase(raw).toLowerCase();
        },
        meta: {
          columnFilterOptions: {
            format: "select",
          },
          columnSelectorOptions: {
            grouping: "configuration",
            description: t(($) => $.perf_reports["The serving status of the advertised {targetType}"], { targetType }),
          },
        },
      }),
      columnHelper.accessor("target_bid_state", {
        id: "target_bid_state",
        header: t(($) => $.perf_reports["Bid State"]),
        size: 130,
        cell: ({ getValue }) => {
          const value = getValue();
          return value != null ? getTargetBidStateLabel(tStr, value) : "";
        },
        meta: {
          columnFilterOptions: { format: "select" },
          columnSelectorOptions: {
            grouping: "configuration",
            description: t(($) => $.perf_reports["The current bid optimization state of the {targetType}"], {
              targetType,
            }),
          },
        },
      }),
      columnHelper.accessor("ad_group_name", {
        id: "ad_group_name",
        header: t(($) => $.perf_reports["Ad Group"]),
        cell: ({ getValue }) => getValue(),
        meta: {
          columnSelectorOptions: {
            grouping: "configuration",
            description: t(($) => $.perf_reports["The ad group the {targetType} belongs to"], { targetType }),
          },
        },
      }),
      columnHelper.accessor("creation_date", {
        id: "creation_date",
        header: t(($) => $.perf_reports["Created On"]),
        cell: ({ getValue }) => dateFormatter.format(new Date(getValue())),
        meta: {
          columnFilterOptions: {
            format: "date",
          },
          columnSelectorOptions: {
            grouping: "configuration",
            description: t(($) => $.perf_reports["The {targetType}'s creation date"], { targetType }),
          },
        },
      }),
      columnHelper.accessor("source", {
        id: "source",
        header: t(($) => $.perf_reports["Source"]),
        cell: ({ getValue }) => {
          const value = getValue();

          return (
            value === "GV_DISCOVERY" ? "initial"
            : value === "harvested" ? "harvesting"
            : value === "harvested_v2" ? "harvesting V2"
            : snakeCaseToSentenceCase(value).toLowerCase()
          );
        },
        meta: {
          columnFilterOptions: {
            format: "select",
          },
          columnSelectorOptions: {
            grouping: "configuration",
            description: t(($) => $.perf_reports["The source of the advertisement target."]),
          },
        },
      }),
      ...standardMetricIds.map((metricId) => columnHelper.metric(metricId)),
    ],
    [standardMetricIds, targetType, dateFormatter, t, tStr],
  );
};
